import api from './authService';
import axios from 'axios';

export const agentsService = {
  // Get all agents
  getAgents: async () => {
    try {
      const response = await api.get('/admin/agents');
      return response.data;
    } catch (error) {
      throw error;
    }
  },

  // Get agent by ID
  getAgent: async (id) => {
    try {
      const response = await api.get(`/admin/agents/${id}`);
      return response.data;
    } catch (error) {
      throw error;
    }
  },

  // Create new agent
  createAgent: async (agentData) => {
    try {
      const response = await api.post('/admin/agents', agentData);
      // Invalidação de caches relacionados
      if (agentsService._summaryCache) agentsService._summaryCache.expiresAt = 0;
      if (agentsService._statsCache) agentsService._statsCache.expiresAt = 0;
      return response.data;
    } catch (error) {
      throw error;
    }
  },

  // Update agent
  updateAgent: async (id, agentData) => {
    try {
      const response = await api.put(`/admin/agents/${id}`, agentData);
      // Invalidação de caches relacionados
      if (agentsService._summaryCache) agentsService._summaryCache.expiresAt = 0;
      if (agentsService._statsCache) agentsService._statsCache.expiresAt = 0;
      return response.data;
    } catch (error) {
      throw error;
    }
  },

  // Delete agent
  deleteAgent: async (id) => {
    try {
      await api.delete(`/admin/agents/${id}`);
      // Invalidação de caches relacionados
      if (agentsService._summaryCache) agentsService._summaryCache.expiresAt = 0;
      if (agentsService._statsCache) agentsService._statsCache.expiresAt = 0;
      return { success: true };
    } catch (error) {
      throw error;
    }
  },

  // Get dashboard statistics
  getDashboardStats: async () => {
    try {
      // Cache curto 30s + deduplicação
      if (!agentsService._statsCache) agentsService._statsCache = { data: null, expiresAt: 0, inflight: null };
      const cache = agentsService._statsCache;
      const now = Date.now();
      if (cache.data && cache.expiresAt > now) return cache.data;
      if (cache.inflight) return await cache.inflight;

      cache.inflight = api.get('/admin/stats').then(res => {
        cache.data = res.data;
        cache.expiresAt = now + 30000;
        cache.inflight = null;
        return res.data;
      }).catch(err => {
        cache.inflight = null;
        throw err;
      });
      return await cache.inflight;
    } catch (error) {
      console.error('Erro ao buscar estatísticas:', error);
      // Fallback para dados mockados se falhar
      return {
        total_agents: 0,
        active_agents: 0,
        total_conversations: 0,
        recent_conversations: 0,
        unique_users: 0,
        total_tokens_30d: 0,
      };
    }
  },

  // Toggle agent status
  toggleAgentStatus: async (agentId) => {
    try {
      const response = await api.post(`/admin/agents/${agentId}/toggle-status`);
      // Invalidação de caches relacionados
      if (agentsService._summaryCache) agentsService._summaryCache.expiresAt = 0;
      if (agentsService._statsCache) agentsService._statsCache.expiresAt = 0;
      if (agentsService._spendingCache) agentsService._spendingCache.clear();
      return response.data;
    } catch (error) {
      throw error;
    }
  },

  // Test agent with a message - USANDO API EXTERNA
  testAgent: async (agentId, message, threadId = null) => {
    try {
      // 🔍 Buscar dados do agente para obter nome e API key
      const agentInfo = await api.get(`/admin/agents/${agentId}`);
      const agentName = agentInfo.data.name;
      
      // 🔑 Buscar API keys disponíveis para este agente
      const apiKeysResponse = await api.get(`/api-keys/by-agent/${encodeURIComponent(agentName)}`);
      const apiKeys = apiKeysResponse.data.api_keys || apiKeysResponse.data.items || [];
      
      if (!apiKeys || apiKeys.length === 0) {
        throw new Error('Nenhuma API key encontrada para este agente. Crie uma API key primeiro.');
      }
      
      // Usar a primeira API key ativa
      const activeApiKey = apiKeys.find(key => key.is_active);
      if (!activeApiKey) {
        throw new Error('Nenhuma API key ativa encontrada para este agente.');
      }
      
      console.log('🔑 [AGENT_TESTER] Usando API Externa com key:', activeApiKey.key_preview);
      
      // 🔐 Buscar chave descriptografada para uso
      const decryptedResponse = await api.get(`/api-keys/${activeApiKey.id}/decrypted`);
      const decryptedKey = decryptedResponse.data.api_key;
      
      console.log('🔐 [DEBUG] Chave descriptografada:', decryptedKey ? `${decryptedKey.substring(0, 15)}...` : 'null');
      console.log('🔐 [DEBUG] Resposta completa:', decryptedResponse.data);
      
      if (!decryptedKey) {
        throw new Error('Erro ao descriptografar API key');
      }
      
      // 🚀 USAR API EXTERNA (/external/v1/chat) - axios puro para evitar interceptors
      const externalApiUrl = api.defaults.baseURL.replace('/api/v1', '') + '/external/v1/chat';
      
      console.log('🌐 [DEBUG] URL da API Externa:', externalApiUrl);
      console.log('🔑 [DEBUG] Header Authorization:', `Bearer ${decryptedKey.substring(0, 15)}...`);
      
      // ✅ USAR AXIOS PURO (sem interceptors que adicionam JWT automaticamente)
      const response = await axios.create().post(externalApiUrl, {
        agent_name: agentName,
        message: message,
        end_user_id: 'test_user_external',
        thread_id: threadId,
        channel: 'api'
      }, {
        headers: {
          'Authorization': `Bearer ${decryptedKey}`,
          'Content-Type': 'application/json'
        },
        timeout: 120000
      });
      
      // 🔍 Verificar headers da resposta para detectar buffer
      const bufferMessages = parseInt(response.headers['x-buffer-messages'] || '1');
      const isBuffered = bufferMessages > 1;
      const messageDelay = parseFloat(response.headers['x-message-delay'] || '0');
      
      if (isBuffered) {
        console.log(`🔄 [AGENT_TESTER] Resposta do buffer HTTP: ${bufferMessages} mensagens combinadas (delay: ${messageDelay}s)`);
      }
      
      const adaptedResponse = {
        answer: response.data.message,
        thread_id: response.data.thread_id,
        agent_name: response.data.agent_name,
        tokens_used: response.data.tokens_used,
        credits_charged: response.data.credits_charged,
        request_id: response.data.request_id,
        processing_time_ms: response.data.processing_time_ms,
        model_used: response.data.model_used,
        // ✅ Indicar se veio do buffer HTTP (para evitar duplicação no frontend)
        is_buffered: isBuffered,
        buffer_messages: bufferMessages,
        message_delay: messageDelay
      };
      
      return adaptedResponse;
    } catch (error) {
      console.error('Erro ao testar agente via API Externa:', error);
      throw new Error('Falha ao testar agente: ' + (error.response?.data?.detail || error.message));
    }
  },

  // Test agent with trace information
  testAgentWithTrace: async (agentId, message, threadId = null) => {
    try {
      // 🔄 MIGRAÇÃO PARA API EXTERNA
      // Buscar dados do agente
      const agentInfo = await api.get(`/admin/agents/${agentId}`);
      const agentName = agentInfo.data.name;

      // Buscar API keys do agente
      const apiKeysResponse = await api.get(`/api-keys/by-agent/${encodeURIComponent(agentName)}`);
      
      // A API retorna um objeto com estrutura: { items: [...], api_keys: [...], pagination: {...}, stats: {...} }
      const apiKeys = apiKeysResponse.data?.api_keys || apiKeysResponse.data?.items || [];
      
      if (!apiKeys || apiKeys.length === 0) {
        throw new Error('Nenhuma API key encontrada para este agente');
      }

      // Pegar a primeira API key ativa
      const activeApiKey = apiKeys.find(key => key.is_active);
      if (!activeApiKey) {
        throw new Error('Nenhuma API key ativa encontrada para este agente');
      }

      // Descriptografar a API key
      const decryptedResponse = await api.get(`/api-keys/${activeApiKey.id}/decrypted`);
      const apiKey = decryptedResponse.data.api_key;

      // Usar API Externa com trace
      const externalApi = axios.create();
      const baseURL = api.defaults.baseURL.replace('/api/v1', '');
      
      const response = await externalApi.post(`${baseURL}/external/v1/chat-trace`, {
        agent_name: agentName,
        message: message,
        end_user_id: 'test_user_trace',
        thread_id: threadId,
        channel: 'api',
        metadata: {}
      }, {
        headers: {
          'Authorization': `Bearer ${apiKey}`,
          'Content-Type': 'application/json'
        }
      });

      return response.data;
    } catch (error) {
      console.error('Erro ao testar agente com trace:', error);
      console.error('Error response:', error.response?.data);
      throw new Error('Falha ao testar agente com trace: ' + (error.response?.data?.detail || error.message));
    }
  },

  // 🆕 Test agent via WebSocket (para testar buffer de mensagens)
  testAgentWebSocket: async (agentId, message, threadId = null) => {
    return new Promise(async (resolve, reject) => {
      try {
        // Buscar dados do agente
        const agentInfo = await api.get(`/admin/agents/${agentId}`);
        const agentName = agentInfo.data.name;
        const company_slug = agentInfo.data.company_slug;

        if (!company_slug) {
          throw new Error('Company slug não encontrado para o agente');
        }

        console.log(`🔌 [WEBSOCKET] Conectando para testar buffer: ${company_slug}/${agentName}`);

        // Criar conexão WebSocket
        const protocol = window.location.protocol === 'https:' ? 'wss:' : 'ws:';
        // 🔧 FIX: Usar porta 8000 do backend ao invés da porta 3000 do frontend
        const backendHost = window.location.hostname + ':8000';
        const wsUrl = `${protocol}//${backendHost}/api/v1/agents/public/${company_slug}/${encodeURIComponent(agentName)}/chat-ws`;
        
        const ws = new WebSocket(wsUrl);
        let responseReceived = false;

        // Timeout de 30 segundos
        const timeout = setTimeout(() => {
          if (!responseReceived) {
            ws.close();
            reject(new Error('Timeout: Agente não respondeu em 30 segundos'));
          }
        }, 30000);

        ws.onopen = () => {
          console.log('🔌 [WEBSOCKET] Conectado - enviando mensagem');
          
          // Enviar mensagem
          const payload = {
            message: message,
            end_user_id: 'test_user_buffer',
            thread_id: threadId,
            metadata: { source: 'agent_tester' }
          };
          
          ws.send(JSON.stringify(payload));
        };

        ws.onmessage = (event) => {
          const data = JSON.parse(event.data);
          console.log('📨 [WEBSOCKET] Recebido:', data);

          if (data.type === 'processing_delayed') {
            // Feedback do buffer - não fazer nada, apenas aguardar
            console.log(`⏱️ [WEBSOCKET] Buffer: ${data.data.message}`);
          } else if (data.type === 'complete') {
            // Resposta final do agente
            responseReceived = true;
            clearTimeout(timeout);
            ws.close();
            
            resolve({
              answer: data.data.answer,
              thread_id: data.data.thread_id,
              streaming: false,
              websocket: true // Flag para identificar que veio do WebSocket
            });
          } else if (data.type === 'error') {
            clearTimeout(timeout);
            ws.close();
            reject(new Error(data.data.error || 'Erro no WebSocket'));
          }
        };

        ws.onerror = (error) => {
          console.error('❌ [WEBSOCKET] Erro:', error);
          clearTimeout(timeout);
          reject(new Error('Erro na conexão WebSocket'));
        };

        ws.onclose = (event) => {
          console.log('🔌 [WEBSOCKET] Desconectado');
          clearTimeout(timeout);
          
          if (!responseReceived) {
            reject(new Error('WebSocket fechado sem resposta'));
          }
        };

      } catch (error) {
        console.error('❌ [WEBSOCKET] Erro ao configurar:', error);
        reject(error);
      }
    });
  },

  // 🆕 Test agent with REAL streaming via Server-Sent Events  
  testAgentStream: async (agentId, message, threadId = null, traceMode = true, onStreamEvent = null) => {
    try {
      // 🔍 Buscar dados do agente para obter nome e API key
      const agentInfo = await api.get(`/admin/agents/${agentId}`);
      const agentName = agentInfo.data.name;
      
      // 🔑 Buscar API keys disponíveis para este agente
      const apiKeysResponse = await api.get(`/api-keys/by-agent/${encodeURIComponent(agentName)}`);
      const apiKeys = apiKeysResponse.data.api_keys || apiKeysResponse.data.items || [];
      
      if (!apiKeys || apiKeys.length === 0) {
        throw new Error('Nenhuma API key encontrada para este agente. Crie uma API key primeiro.');
      }
      
      // Usar a primeira API key ativa
      const activeApiKey = apiKeys.find(key => key.is_active);
      if (!activeApiKey) {
        throw new Error('Nenhuma API key ativa encontrada para este agente.');
      }

      console.log('🚀 [STREAMING] Usando API Externa com key:', activeApiKey.key_preview);
      
      // 🔐 Buscar chave descriptografada para uso
      const decryptedResponse = await api.get(`/api-keys/${activeApiKey.id}/decrypted`);
      const decryptedKey = decryptedResponse.data.api_key;

      // 🚀 USAR API EXTERNA STREAMING (/external/v1/chat?streaming=true) - URL absoluta
      const streamUrl = api.defaults.baseURL.replace('/api/v1', '') + '/external/v1/chat?streaming=true';
      
      // Construir payload
      const payload = {
        agent_name: agentName,
        message: message,
        end_user_id: 'test_user_stream_external',
        thread_id: threadId,
        channel: 'api'
      };

      // Retornar Promise que será resolvida quando streaming completar
      return new Promise((resolve, reject) => {
        let finalResult = null;
        let accumulatedText = '';
        
        // ✅ USAR FETCH PURO (sem interceptors automáticos do axios)
        fetch(streamUrl, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${decryptedKey}`,  // ✅ API Externa auth descriptografada
            'Accept': 'text/event-stream',
            'Cache-Control': 'no-cache'
          },
          body: JSON.stringify(payload)
        })
        .then(response => {
          if (!response.ok) {
            throw new Error(`HTTP ${response.status}: ${response.statusText}`);
          }

          const reader = response.body.getReader();
          const decoder = new TextDecoder();
          let buffer = ''; // 🔧 Buffer para JSON fragmentado

          function readStream() {
            reader.read().then(({ done, value }) => {
              if (done) {
                console.log('🏁 [STREAMING] Stream finalizado');
                if (finalResult) {
                  resolve(finalResult);
                } else {
                  // 🔧 Reset do estado de streaming no componente
                  if (onStreamEvent) {
                    onStreamEvent({ type: 'stream_error', error: 'Stream finalizado sem resultado final' });
                  }
                  reject(new Error('Stream finalizado sem resultado final'));
                }
                return;
              }

              // 🔧 Decodificar chunk e adicionar ao buffer
              const chunk = decoder.decode(value, { stream: true });
              buffer += chunk;
              
              // 🔧 Processar linhas completas do buffer
              const lines = buffer.split('\n');
              buffer = lines.pop() || ''; // Manter linha incompleta no buffer

              for (const line of lines) {
                if (line.startsWith('data: ')) {
                  try {
                    const jsonString = line.substring(6).trim();
                    if (!jsonString) continue; // Ignorar linhas vazias
                    
                    const eventData = JSON.parse(jsonString);
                    
                    console.log('📡 [STREAMING] Evento:', eventData.type);
                    
                    // Processar diferentes tipos de eventos
                    if (eventData.type === 'token' || eventData.type === 'text_delta') {
                      // Token individual - acumular texto
                      const token = eventData.data?.token || eventData.data?.text || '';
                      accumulatedText += token;
                      
                      // Callback para atualizar UI progressivamente
                      if (onStreamEvent) {
                        onStreamEvent({
                          type: 'token',
                          token: token,
                          accumulatedText: accumulatedText
                        });
                      }
                    } else if (eventData.type === 'complete') {
                      // 🔧 CORREÇÃO: Usar estrutura correta do backend
                      // Backend retorna: data.result.final_output, não data.answer
                      const resultData = eventData.data?.result || {};
                      const billingData = eventData.data?.billing || resultData.billing_info || {};
                      const traceData = eventData.data?.trace || resultData.trace_data || null;
                      
                      finalResult = {
                        // 🔧 Usar final_output (backend) ao invés de answer
                        answer: resultData.final_output || accumulatedText,
                        thread_id: eventData.data?.thread_id,
                        streaming: true,
                        trace: traceData,
                        billing: billingData  // 🆕 Incluir billing se disponível
                      };
                      
                      console.log('✅ [STREAMING] Resultado final recebido');
                      console.log('🔍 [STREAMING] Final output:', finalResult.answer ? 'PRESENTE' : 'AUSENTE');
                      console.log('🔍 [STREAMING] Trace data:', finalResult.trace ? 'PRESENTE' : 'AUSENTE');
                      console.log('💰 [STREAMING] Billing data:', finalResult.billing ? 'PRESENTE' : 'AUSENTE');
                      
                      // Callback final
                      if (onStreamEvent) {
                        onStreamEvent({
                          type: 'complete',
                          finalAnswer: finalResult.answer,
                          thread_id: finalResult.thread_id,
                          trace: finalResult.trace,
                          billing: finalResult.billing  // 🆕 Incluir billing no callback
                        });
                      }
                    } else if (eventData.type === 'error') {
                      console.error('❌ [STREAMING] Erro recebido:', eventData.data);
                      // 🔧 Reset do estado de streaming no componente
                      if (onStreamEvent) {
                        onStreamEvent({ type: 'stream_error', error: eventData.data?.error || 'Erro no streaming' });
                      }
                      reject(new Error(eventData.data?.error || 'Erro no streaming'));
                      return;
                    }
                  } catch (parseError) {
                    console.warn('⚠️ [STREAMING] Erro ao parsear evento:', line);
                    console.warn('⚠️ [STREAMING] Parse error details:', parseError.message);
                    // 🔧 NÃO rejeitar por erro de parsing - apenas logar e continuar
                    // Isso evita que o streaming pare por JSON malformado
                  }
                }
              }

              // Continuar lendo stream
              readStream();
            }).catch((readError) => {
              console.error('❌ [STREAMING] Erro na leitura do stream:', readError);
              // 🔧 Reset do estado de streaming no componente
              if (onStreamEvent) {
                onStreamEvent({ type: 'stream_error', error: readError.message });
              }
              reject(readError);
            });
          }

          // Iniciar leitura
          readStream();
        })
        .catch(reject);
      });

    } catch (error) {
      console.error('Erro ao iniciar streaming:', error);
      throw new Error('Falha ao iniciar streaming: ' + error.message);
    }
  },

  // Get agent performance diagnostics
  getAgentDiagnostics: async (agentId, period = '7d') => {
    try {
      const response = await api.get(`/admin/agents/${agentId}/diagnostics?period=${period}`);
      return response.data;
    } catch (error) {
      console.error('Erro ao buscar diagnóstico:', error);
      throw new Error('Falha ao buscar diagnóstico: ' + (error.response?.data?.detail || error.message));
    }
  },

  // Get company credits balance
  getCompanyCredits: async () => {
    try {
      if (!agentsService._creditsCache) agentsService._creditsCache = { data: null, expiresAt: 0, inflight: null };
      const cache = agentsService._creditsCache;
      const now = Date.now();
      if (cache.data && cache.expiresAt > now) return cache.data;
      if (cache.inflight) return await cache.inflight;

      cache.inflight = api.get('/admin/company-credits').then(res => {
        cache.data = res.data;
        cache.expiresAt = now + 60000; // 60s
        cache.inflight = null;
        return res.data;
      }).catch(err => {
        cache.inflight = null;
        throw err;
      });
      return await cache.inflight;
    } catch (error) {
      console.error('Erro ao buscar saldo de créditos:', error);
      throw new Error('Falha ao buscar saldo de créditos: ' + (error.response?.data?.detail || error.message));
    }
  },

  // Get agents spending data for chart
  getAgentsSpending: async (period = '1m') => {
    try {
      if (!agentsService._spendingCache) agentsService._spendingCache = new Map();
      const key = `sp_${period}`;
      const now = Date.now();
      const cached = agentsService._spendingCache.get(key);
      if (cached && cached.expiresAt > now) return cached.data;
      if (cached?.inflight) return await cached.inflight;

      const inflight = api.get(`/admin/agents-spending?period=${period}`).then(res => {
        agentsService._spendingCache.set(key, { data: res.data, expiresAt: now + 30000, inflight: null });
        return res.data;
      }).catch(err => {
        const c = agentsService._spendingCache.get(key);
        if (c) agentsService._spendingCache.set(key, { ...c, inflight: null });
        throw err;
      });
      agentsService._spendingCache.set(key, { data: null, expiresAt: 0, inflight });
      return await inflight;
    } catch (error) {
      console.error('Erro ao buscar gastos por agente:', error);
      throw new Error('Falha ao buscar dados de gastos: ' + (error.response?.data?.detail || error.message));
    }
  },

  // Get cache status
  getCacheStatus: async () => {
    try {
      const response = await api.get('/admin/agents/cache/status');
      return response.data;
    } catch (error) {
      console.error('Erro ao buscar status do cache:', error);
      throw new Error('Falha ao buscar status do cache: ' + (error.response?.data?.detail || error.message));
    }
  },

  // Invalidate cache
  invalidateCache: async () => {
    try {
      const response = await api.post('/admin/agents/cache/invalidate');
      return response.data;
    } catch (error) {
      console.error('Erro ao invalidar cache:', error);
      throw new Error('Falha ao invalidar cache: ' + (error.response?.data?.detail || error.message));
    }
  },

  // Validate agent configuration
  validateAgent: async (agentData) => {
    try {
      // Client-side validation
      const errors = [];
      
      if (!agentData.name?.trim()) {
        errors.push('Nome é obrigatório');
      }
      
      if (!agentData.instructions?.trim()) {
        errors.push('Instruções são obrigatórias');
      }
      
      if (!agentData.llm_config?.model) {
        errors.push('Modelo LLM é obrigatório');
      }
      
      if (agentData.llm_config?.temperature < 0 || agentData.llm_config?.temperature > 2) {
        errors.push('Temperature deve estar entre 0 e 2');
      }
      
      if (agentData.llm_config?.max_tokens < 1 || agentData.llm_config?.max_tokens > 4000) {
        errors.push('Max tokens deve estar entre 1 e 4000');
      }
      
      return {
        valid: errors.length === 0,
        errors,
      };
    } catch (error) {
      console.error('Erro na validação:', error);
      return {
        valid: false,
        errors: ['Erro na validação'],
      };
    }
  },



  // Clone agent
  cloneAgent: async (agentId, newName) => {
    try {
      const originalAgent = await agentsService.getAgent(agentId);
      
      const clonedData = {
        ...originalAgent,
        name: newName || `${originalAgent.name} (Cópia)`,
        id: undefined, // Remove ID para criar novo
        created_at: undefined,
        updated_at: undefined,
        is_active: false, // Clone starts as inactive
      };
      
      const response = await agentsService.createAgent(clonedData);
      return response;
    } catch (error) {
      console.error('Erro ao clonar agente:', error);
      throw new Error('Falha ao clonar agente');
    }
  },

  // 🗑️ CÓDIGO MORTO REMOVIDO:
  // ❌ exportAgent() - ZERO chamadas encontradas
  // ❌ importAgent() - ZERO chamadas encontradas  
  // ❌ getAgentMetrics() - ZERO chamadas encontradas

  // Get available LLM models from database
  getAvailableModels: async () => {
    try {
      const response = await api.get('/admin/available-models');
      return response.data;
    } catch (error) {
      console.error('Erro ao buscar modelos disponíveis:', error);
      // Fallback para modelos padrão em caso de erro
      return [
        { value: 'gpt-4o-mini', label: 'GPT-4o Mini' },
        { value: 'gpt-4o', label: 'GPT-4o' },
        { value: 'gpt-4-turbo', label: 'GPT-4 Turbo' },
      ];
    }
  },

  // Get available models filtered by type (NEW - Phase 5)
  getAvailableModelsByType: async (modelType) => {
    try {
      // Cache simples por sessão (60s)
      if (!agentsService._modelsCache) agentsService._modelsCache = new Map();
      const cacheKey = `models_${modelType}`;
      const cached = agentsService._modelsCache.get(cacheKey);
      if (cached && cached.expiresAt > Date.now()) return cached.data;

      const response = await api.get(`/admin/available-models?model_type=${modelType}`);
      agentsService._modelsCache.set(cacheKey, { data: response.data, expiresAt: Date.now() + 60000 });
      return response.data;
    } catch (error) {
      console.error(`Erro ao buscar modelos do tipo ${modelType}:`, error);
      
      // Fallback específico por tipo
      if (modelType === 'embedding') {
        return [
          { value: 'text-embedding-3-small', label: 'text-embedding-3-small (1536 dim)' },
          { value: 'text-embedding-3-large', label: 'text-embedding-3-large (3072 dim)' },
        ];
      } else {
        // Fallback para LLMs (padrão)
        return [
          { value: 'gpt-4o-mini', label: 'GPT-4o Mini' },
          { value: 'gpt-4o', label: 'GPT-4o' },
          { value: 'gpt-4-turbo', label: 'GPT-4 Turbo' },
        ];
      }
    }
  },

  // Leve: lista de agentes para cards (cache 60s)
  getAgentsSummary: async () => {
    try {
      if (!agentsService._summaryCache) agentsService._summaryCache = { data: null, expiresAt: 0 };
      if (agentsService._summaryCache.data && agentsService._summaryCache.expiresAt > Date.now()) {
        return agentsService._summaryCache.data;
      }

      const response = await api.get('/admin/agents/summary');
      agentsService._summaryCache = { data: response.data, expiresAt: Date.now() + 60000 };
      return response.data;
    } catch (error) {
      console.error('Erro ao buscar lista leve de agentes:', error);
      throw error;
    }
  },

  // Sessions summary (lista leve) por agente/empresa
  getSessionsSummary: async (agentId = null, limit = 5) => {
    try {
      if (!agentsService._sessionsSummaryCache) agentsService._sessionsSummaryCache = new Map();
      const key = `sess_${agentId || 'all'}_${limit}`;
      const now = Date.now();
      const cached = agentsService._sessionsSummaryCache.get(key);
      if (cached && cached.expiresAt > now) return cached.data;
      if (cached?.inflight) return await cached.inflight;

      const params = { limit };
      if (agentId) params.agent_id = agentId;
      const inflight = api.get('/admin/sessions/summary', { params }).then(res => {
        agentsService._sessionsSummaryCache.set(key, { data: res.data, expiresAt: now + 30000, inflight: null });
        return res.data;
      }).catch(err => {
        const c = agentsService._sessionsSummaryCache.get(key);
        if (c) agentsService._sessionsSummaryCache.set(key, { ...c, inflight: null });
        throw err;
      });
      agentsService._sessionsSummaryCache.set(key, { data: null, expiresAt: 0, inflight });
      return await inflight;
    } catch (error) {
      console.error('Erro ao buscar sessions summary:', error);
      throw error;
    }
  },

  // Mensagens de uma sessão específica (admin)
  getSessionMessages: async (endUserId, threadId, limit = 50) => {
    try {
      const response = await api.get(`/admin/sessions/${encodeURIComponent(endUserId)}/${encodeURIComponent(threadId)}`, { params: { limit } });
      return response.data?.messages || [];
    } catch (error) {
      console.error('Erro ao buscar mensagens da sessão:', error);
      throw error;
    }
  },

  // Get default model
  getDefaultModel: async (modelType = 'llm') => {
    try {
      const response = await api.get(`/admin/default-model?model_type=${modelType}`);
      return response.data.default_model;
    } catch (error) {
      console.error('Erro ao buscar modelo padrão:', error);
      // Fallback baseado no tipo
      return modelType === 'embedding' ? 'text-embedding-3-small' : 'gpt-4o-mini';
    }
  },
}; 